import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { inputCls } from "@/components/Modal";

export default function ForcePasswordChange() {
  const { refreshUser, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 10) { setError("New password must be at least 10 characters."); return; }
    if (form.next !== form.confirm) { setError("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/change-password", { current_password: form.current, new_password: form.next });
      await refreshUser();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#121211] px-6 text-[#F9F9F8]" data-testid="force-password-change">
      <div className="w-full max-w-sm">
        <Brand logoHeight="h-12" mediaSize="text-[12px]" />
        <div className="mt-8 font-display text-3xl tracking-tight">Set a new password</div>
        <p className="mt-2 text-sm text-[#A1A19D]">
          For security, you must replace the temporary password you were given before accessing the workspace.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="force-password-form">
          <div>
            <label className="cm-label">Current (temporary) password</label>
            <input data-testid="fpc-current" type="password" autoComplete="current-password" className={inputCls}
              value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required />
          </div>
          <div>
            <label className="cm-label">New password</label>
            <input data-testid="fpc-next" type="password" autoComplete="new-password" className={inputCls}
              value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required />
          </div>
          <div>
            <label className="cm-label">Confirm new password</label>
            <input data-testid="fpc-confirm" type="password" autoComplete="new-password" className={inputCls}
              value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
          </div>
          {error && <div className="text-sm text-[#e07a7a]" data-testid="fpc-error">{error}</div>}
          <button data-testid="fpc-submit" type="submit" disabled={busy}
            className="w-full rounded-sm bg-[#F59E0B] px-4 py-2.5 text-sm font-semibold text-[#121211] transition-colors hover:bg-[#e8940a] disabled:opacity-60">
            {busy ? "Saving…" : "Set password & continue"}
          </button>
          <button data-testid="fpc-signout" type="button" onClick={logout}
            className="w-full text-xs text-[#A1A19D] underline-offset-2 hover:underline">
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
